import { formatNative, formatUsd } from './optionsDashboardUtils';
import { formatPercent, formatSignedNative, formatSignedUsd, getCurrencyDigits, getValueTone, sanitizeTestId } from './optionsPerformanceUtils';
import { buildStrategyFilterOptions, formatStrategyLabel } from './optionsStrategyFilterUtils';
import { formatLocalizedNumber } from './optionsLocaleFormatting';

export const ANALYTICS_CURRENCIES = ['usd', 'btc', 'eth'];
export const ANALYTICS_RANGES = ['30d', '90d', 'ytd', 'all', 'custom'];
export const ANALYTICS_UNDERLYINGS = ['all', 'btc', 'eth', 'usdc'];

const formatFeeUsd = (value) => {
  if (value === null || value === undefined) return '—';
  const numeric = Number(value);
  if (Math.abs(numeric) < 1e-12) return formatUsd(0);
  return `-${formatUsd(Math.abs(numeric))}`;
};

const formatFeeNative = (value, currency) => {
  if (value === null || value === undefined) return '—';
  const numeric = Number(value);
  const formatted = formatNative(Math.abs(numeric), currency, getCurrencyDigits(currency));
  if (Math.abs(numeric) < 1e-12) return formatted;
  return `-${formatted}`;
};

const buildBreakdownMap = (items = []) => new Map(items.map((item) => [String(item.currency).toUpperCase(), item]));

const formatAbsoluteUsd = (value) => {
  if (value === null || value === undefined) return '—';
  return formatUsd(Math.abs(Number(value)));
};

const formatAbsoluteNative = (value, currency) => {
  if (value === null || value === undefined) return '—';
  return formatNative(Math.abs(Number(value)), currency, getCurrencyDigits(currency));
};

const getPrimaryCurrency = ({ selectedCurrency, rowUnderlying = null, metrics }) => {
  if (rowUnderlying) return String(rowUnderlying).toUpperCase();
  if (selectedCurrency === 'usd') return null;
  const requested = String(selectedCurrency).toUpperCase();
  if ((metrics?.by_currency || []).length === 1 && metrics.by_currency[0]?.currency === requested) {
    return requested;
  }
  return null;
};

export const resolveAnalyticsPrimaryBreakdownItem = ({ metrics, selectedCurrency, rowUnderlying = null }) => {
  const breakdownMap = buildBreakdownMap(metrics?.by_currency || []);
  const primaryCurrency = getPrimaryCurrency({ selectedCurrency, rowUnderlying, metrics });
  return primaryCurrency ? breakdownMap.get(primaryCurrency) || null : null;
};

export const buildAnalyticsMetricDisplay = ({ metrics, selectedCurrency, metricKey, rowUnderlying = null }) => {
  const breakdownMap = buildBreakdownMap(metrics?.by_currency || []);
  const primaryCurrency = getPrimaryCurrency({ selectedCurrency, rowUnderlying, metrics });
  const breakdownItem = primaryCurrency ? breakdownMap.get(primaryCurrency) : null;

  if (metricKey === 'operations') {
    return { primary: String(metrics?.operations_count ?? 0), secondary: null, toneClass: 'text-white' };
  }

  if (metricKey === 'winRate') {
    return { primary: formatPercent(metrics?.win_rate), secondary: null, toneClass: 'text-white' };
  }

  const usdField = metricKey === 'avgPnl' ? metrics?.avg_pnl_usd : metricKey === 'fees' ? metrics?.fees_usd : metrics?.trading_pnl_usd;
  const nativeField = breakdownItem
    ? metricKey === 'avgPnl'
      ? breakdownItem.avg_pnl_native
      : metricKey === 'fees'
        ? breakdownItem.fees_native
        : breakdownItem.pnl_native
    : null;

  if (metricKey === 'fees') {
    if (primaryCurrency && breakdownItem) {
      return {
        primary: formatFeeNative(nativeField, primaryCurrency),
        secondary: usdField === null || usdField === undefined ? null : `≈ ${formatUsd(Math.abs(Number(usdField)))}`,
        toneClass: 'text-white',
      };
    }
    return {
      primary: formatFeeUsd(usdField),
      secondary: buildBreakdownSecondary(metrics, metricKey),
      toneClass: 'text-white',
    };
  }

  if (primaryCurrency && breakdownItem) {
    const primary = formatSignedNative(nativeField, primaryCurrency, getCurrencyDigits(primaryCurrency));
    return {
      primary,
      secondary: usdField === null || usdField === undefined ? null : `≈ ${formatUsd(Math.abs(Number(usdField)))}`,
      toneClass: getValueTone(nativeField),
    };
  }

  return {
    primary: formatSignedUsd(usdField),
    secondary: buildBreakdownSecondary(metrics, metricKey),
    toneClass: getValueTone(usdField),
  };
};

export const buildAnalyticsValueDisplay = ({ metrics, selectedCurrency, usdField, nativeField, rowUnderlying = null, absolute = false }) => {
  const breakdownMap = buildBreakdownMap(metrics?.by_currency || []);
  const primaryCurrency = getPrimaryCurrency({ selectedCurrency, rowUnderlying, metrics });
  const breakdownItem = primaryCurrency ? breakdownMap.get(primaryCurrency) : null;
  const usdValue = metrics?.[usdField];
  const nativeValue = breakdownItem?.[nativeField];

  if (primaryCurrency && breakdownItem && nativeValue !== null && nativeValue !== undefined) {
    return {
      primary: absolute ? formatAbsoluteNative(nativeValue, primaryCurrency) : formatSignedNative(nativeValue, primaryCurrency, getCurrencyDigits(primaryCurrency)),
      secondary: usdValue === null || usdValue === undefined ? null : `≈ ${absolute ? formatAbsoluteUsd(usdValue) : formatSignedUsd(usdValue)}`,
      toneClass: absolute ? 'text-white' : getValueTone(nativeValue),
    };
  }

  return {
    primary: absolute ? formatAbsoluteUsd(usdValue) : formatSignedUsd(usdValue),
    secondary: buildBreakdownSecondaryForField(metrics, nativeField, absolute),
    toneClass: absolute ? 'text-white' : getValueTone(usdValue),
  };
};

export const buildBreakdownSecondary = (metrics, metricKey) => {
  const items = metrics?.by_currency || [];
  if (!items.length) return null;
  return items.map((item) => {
    const currency = String(item.currency).toUpperCase();
    if (metricKey === 'fees') return `${currency} ${formatFeeNative(item.fees_native, currency)}`;
    if (metricKey === 'avgPnl') return `${currency} ${formatSignedNative(item.avg_pnl_native, currency, getCurrencyDigits(currency))}`;
    return `${currency} ${formatSignedNative(item.pnl_native, currency, getCurrencyDigits(currency))}`;
  }).join(' · ');
};

export const buildBreakdownSecondaryForField = (metrics, nativeField, absolute = false) => {
  const items = metrics?.by_currency || [];
  if (!items.length) return null;
  return items
    .filter((item) => item?.[nativeField] !== null && item?.[nativeField] !== undefined)
    .map((item) => {
      const currency = String(item.currency).toUpperCase();
      const formatted = absolute
        ? formatAbsoluteNative(item[nativeField], currency)
        : formatSignedNative(item[nativeField], currency, getCurrencyDigits(currency));
      return `${currency} ${formatted}`;
    })
    .join(' · ');
};

export const formatAnalyticsRatio = (value, digits = 2) => {
  if (value === null || value === undefined) return '—';
  return `${formatLocalizedNumber(Number(value), { minimumFractionDigits: digits, maximumFractionDigits: digits })}x`;
};

export const formatAnalyticsNumber = (value, digits = 2) => {
  if (value === null || value === undefined) return '—';
  return formatLocalizedNumber(Number(value), { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

export const formatAnalyticsDays = (value, digits = 1) => {
  if (value === null || value === undefined) return '—';
  return `${formatLocalizedNumber(Number(value), { minimumFractionDigits: digits, maximumFractionDigits: digits })}d`;
};

export const getRatioTone = (value, neutral = 1) => {
  if (value === null || value === undefined) return 'text-white';
  if (Number(value) > neutral) return 'text-[#22C55E]';
  if (Number(value) < neutral) return 'text-[#EF4444]';
  return 'text-white';
};

export const buildCycleMetricDisplay = ({ valueUsd, byCurrency = [], selectedCurrency, rowUnderlying = null }) => {
  const breakdownMap = buildBreakdownMap(byCurrency);
  const preferred = getPrimaryCurrency({ selectedCurrency, rowUnderlying, metrics: { by_currency: byCurrency } });
  const native = preferred ? breakdownMap.get(preferred)?.pnl_native : null;
  if (preferred && native !== null && native !== undefined) {
    return {
      primary: formatSignedNative(native, preferred, getCurrencyDigits(preferred)),
      secondary: valueUsd === null || valueUsd === undefined ? null : `≈ ${formatUsd(Math.abs(Number(valueUsd)))}`,
      toneClass: getValueTone(native),
    };
  }
  return {
    primary: formatSignedUsd(valueUsd),
    secondary: byCurrency.length ? byCurrency.map((item) => `${item.currency} ${formatSignedNative(item.pnl_native, item.currency, getCurrencyDigits(item.currency))}`).join(' · ') : null,
    toneClass: getValueTone(valueUsd),
  };
};

export const buildGreekDisplay = (value, currency) => ({
  primary: value === null || value === undefined ? '—' : formatSignedNative(value, currency, getCurrencyDigits(currency)),
  toneClass: 'text-white',
});

export const buildAnalyticsStrategyOptions = (strategyRows = []) => buildStrategyFilterOptions(
  strategyRows
    .filter((row) => Number(row?.metrics?.operations_count || 0) > 0)
    .map((row) => row?.label)
);

export { formatStrategyLabel, sanitizeTestId };